'use client';

import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy } from 'lucide-react';
import { competitors } from '@/lib/mock-data';

export function ComparisonTable() {
  return (
    <section className="py-24 bg-[#1a1a1a]">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Why Choose Yokki?
          </h2>
          <p className="text-xl text-[#b9bbbe] max-w-2xl mx-auto">
            Compare Yokki with other popular Discord bots
          </p>
        </motion.div>

        {/* Comparison table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-5xl mx-auto"
        >
          <div className="overflow-x-auto">
            <Card className="bg-[#252525] border-[#b9bbbe]/10">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#b9bbbe]/10">
                    <th className="text-left p-6 text-[#b9bbbe] font-semibold">Bot</th>
                    <th className="text-left p-6 text-[#b9bbbe] font-semibold">Setup</th>
                    <th className="text-left p-6 text-[#b9bbbe] font-semibold">Price</th>
                    <th className="text-left p-6 text-[#b9bbbe] font-semibold">Protection</th>
                    <th className="text-left p-6 text-[#b9bbbe] font-semibold">All-in-One</th>
                  </tr>
                </thead>
                <tbody>
                  {competitors.map((competitor, index) => (
                    <motion.tr
                      key={competitor.name}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className={`border-b border-[#b9bbbe]/10 ${
                        competitor.name === 'Yotti'
                          ? 'bg-gradient-to-r from-[#5865F2]/20 to-transparent'
                          : 'hover:bg-[#2a2a2a] transition-colors'
                      }`}
                    >
                      <td className="p-6">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-bold text-lg">{competitor.name}</span>
                          {competitor.name === 'Yokki' && (
                            <Badge className="bg-[#5865F2] text-white">
                              <Trophy className="h-3 w-3 mr-1" />
                              Best Choice
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-6 text-white">{competitor.setup}</td>
                      <td className="p-6 text-white">{competitor.price}</td>
                      <td className="p-6 text-white">{competitor.protection}</td>
                      <td className="p-6 text-white">{competitor.allInOne}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          {/* Key differentiators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 grid md:grid-cols-3 gap-6"
          >
            <Card className="bg-[#252525] border-[#5865F2]/30 p-6 text-center">
              <div className="text-3xl font-bold text-[#5865F2] mb-2">100%</div>
              <div className="text-white font-semibold mb-1">Completely Free</div>
              <div className="text-sm text-[#b9bbbe]">
                No hidden costs or premium tiers
              </div>
            </Card>

            <Card className="bg-[#252525] border-[#3ba55d]/30 p-6 text-center">
              <div className="text-3xl font-bold text-[#3ba55d] mb-2">2min</div>
              <div className="text-white font-semibold mb-1">Quick Setup</div>
              <div className="text-sm text-[#b9bbbe]">
                Get started faster than any competitor
              </div>
            </Card>

            <Card className="bg-[#252525] border-[#5865F2]/30 p-6 text-center">
              <div className="text-3xl font-bold text-[#5865F2] mb-2">4-in-1</div>
              <div className="text-white font-semibold mb-1">All-in-One Solution</div>
              <div className="text-sm text-[#b9bbbe]">
                Security, moderation, fun, and utility combined
              </div>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
